import { Fragment, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ViewProductModels from './ViewProductModels'
import AddEditProductModels from './AddEditProductModels'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, EnabledIcon, DisabledIcon } from '../../../icons'
import { productModelsDeletePermissions, productModelsWritePermissions } from '../../../utils/permissionValidation'
import { clearProductModelsMessages, loadProductModels, removeProductModels } from '../../../actions/productModels'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'name',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'name',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'productType',
    order: 'asc',
    translationKey: 'product_type_asc',
  },
  {
    field: 'productType',
    order: 'desc',
    translationKey: 'product_type_desc',
  },
  {
    field: 'category',
    order: 'asc',
    translationKey: 'category_asc',
  },
  {
    field: 'category',
    order: 'desc',
    translationKey: 'category_desc',
  },
  {
    field: 'deviceFunction',
    order: 'asc',
    translationKey: 'device_function_asc',
  },
  {
    field: 'deviceFunction',
    order: 'desc',
    translationKey: 'device_function_desc',
  },
  {
    field: 'technology',
    order: 'asc',
    translationKey: 'technology_asc',
  },
  {
    field: 'technology',
    order: 'desc',
    translationKey: 'technology_desc',
  },
  {
    field: 'isActive',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'isActive',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Models = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [productModelsTableData, setProductModelsTableData] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [selectedFilterProductType, setSelectedFilterProductType] = useState(null)
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null)
  const [selectedFilterDeviceFunction, setSelectedFilterDeviceFunction] = useState(null)
  const [selectedBrandFilter, setSelectedBrandFilter] = useState([])
  const [selectedTechnologyFilter, setSelectedTechnologyFilter] = useState([])
  const [statusesFilter, setStatusesFilter] = useState([])
  const [selectedModelDetails, setSelectedModelDetails] = useState(null)
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedVariant, setExpandedVariant] = useState(0)
  const [showAddEditProductModelsModal, setShowAddEditProductModelsModal] = useState(false)
  const [showViewProductModelsModal, setShowViewProductModelsModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [modelIdToDelete, setModelIdToDelete] = useState(null)
  const Statuses = ['active', 'inactive']

  const brandLabels = props.brands?.map((b) => {
    return b.brand || []
  })
  const categoryLabels = props.productCategories?.map((pc) => {
    return pc.category || []
  })
  const productTypeLabels = props.productTypes?.map((pt) => {
    return pt.productType || []
  })
  const modelLabels = props.models?.map((m) => {
    return m.model || []
  })
  const deviceFunctionLabels = props.deviceFunctions?.map((df) => {
    return df.deviceFunction || []
  })
  const technologyLabels = props.technologies?.map((t) => {
    return t.technology || []
  })

  const brandNameToId = useMemo(() => {
    const map = new Map()
    ;(props.brands || []).forEach((b) => {
      if (b?.brand && b?.id != null) map.set(b.brand, b.id)
    })
    return map
  }, [props.brands])

  const categoryNameToId = useMemo(() => {
    const map = new Map()
    ;(props.productCategories || []).forEach((c) => {
      const name = c?.category || c?.name
      if (name && c?.id != null) map.set(name, c.id)
    })
    return map
  }, [props.productCategories])

  const productTypeNameToId = useMemo(() => {
    const map = new Map()
    ;(props.productTypes || []).forEach((pt) => {
      const name = pt?.productType || pt?.name
      if (name && pt?.id != null) map.set(name, pt.id)
    })
    return map
  }, [props.productTypes])

  const modelNameToId = useMemo(() => {
    const map = new Map()
    ;(props.models || []).forEach((m) => {
      const name = m?.model || m?.name
      if (name && m?.id != null) map.set(name, m.id)
    })
    return map
  }, [props.models])

  const deviceFunctionNameToId = useMemo(() => {
    const map = new Map()
    ;(props.deviceFunctions || []).forEach((df) => {
      const name = df?.deviceFunction || df?.name
      if (name && df?.id != null) map.set(name, df.id)
    })
    return map
  }, [props.deviceFunctions])

  const technologyNameToId = useMemo(() => {
    const map = new Map()
    ;(props.technologies || []).forEach((tech) => {
      const name = tech?.technology || tech?.name
      if (name && tech?.id != null) map.set(name, tech.id)
    })
    return map
  }, [props.technologies])

  const toId = (map, selectedName) => {
    if (!selectedName) return null
    return map.get(selectedName) ?? null
  }
  const toIds = (map, selectedNames = []) => selectedNames.map((n) => map.get(n)).filter((id) => id != null)

  useEffect(() => {
    const tableData = props.productModelsData?.map((item, index) => {
      return {
        index,
        _original: item,
        name: item.name || '',
        productType: item.productType?.name || '',
        category: item.category?.name || '',
        deviceFunction: item.deviceFunction?.name || '',
        brand: item.brand?.name || '',
        tokenActivation: item.tokenActivation?.name ? t('yes') : t('no'),
        dashboardAccess: item.dashboardAccess ? t('yes') : t('no'),
        coreUnits: item.coreUnit ? t('yes') : t('no'),
        technology: item.technology?.name || '',
        status: item.isActive ? Statuses[0] : Statuses[1] || '',
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item, true)
            },
            requiredPermission: productModelsWritePermissions,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(item)
            },
            requiredPermission: productModelsDeletePermissions,
            isDisabled: item.isActive,
          },
        ],
      }
    })

    setProductModelsTableData(tableData)
  }, [props.productModelsData])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.errorMessage, props.actions])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.successMessage, props.actions])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    const changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadTableData(true)
        }
        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [
    searchParam,
    selectedFilterModel,
    selectedFilterProductType,
    selectedFilterCategory,
    selectedTechnologyFilter,
    selectedFilterDeviceFunction,
    selectedBrandFilter,
    statusesFilter,
    orderBy,
  ])

  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams()
    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }
    props.actions.loadProductModels(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 1) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel(null)
    setSelectedFilterProductType(null)
    setSelectedFilterCategory(null)
    setSelectedFilterDeviceFunction(null)
    setSelectedBrandFilter([])
    setStatusesFilter([])
    setSelectedTechnologyFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) queryParams = { ...queryParams, query: searchParam }

    if (selectedFilterModel) {
      queryParams = { ...queryParams, name: selectedFilterModel }
    }

    if (selectedFilterProductType) {
      queryParams = { ...queryParams, productTypeId: toId(productTypeNameToId, selectedFilterProductType) }
    }

    if (selectedFilterCategory) {
      queryParams = { ...queryParams, categoryId: toId(categoryNameToId, selectedFilterCategory) }
    }

    if (selectedFilterDeviceFunction) {
      queryParams = { ...queryParams, deviceFunctionId: toId(deviceFunctionNameToId, selectedFilterDeviceFunction) }
    }

    if (selectedBrandFilter.length > 0) {
      queryParams = { ...queryParams, brandId: toIds(brandNameToId, selectedBrandFilter) }
    }

    if (selectedTechnologyFilter.length > 0) {
      queryParams = { ...queryParams, technologyId: toIds(technologyNameToId, selectedTechnologyFilter) }
    }

    if (statusesFilter.length === 1) {
      queryParams = { ...queryParams, isActive: statusesFilter[0] === Statuses[0] }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEdit = (modelDetails, isEditModal) => {
    setSelectedModelDetails(modelDetails)
    isEditModal ? setShowAddEditProductModelsModal(true) : setShowViewProductModelsModal(true)
  }

  const showConfirmationModal = (item) => {
    setModelIdToDelete(item)
    setShowDeleteConfirmModal(true)
  }

  const handleDeleteModel = () => {
    setShowDeleteConfirmModal(false)
    props.actions.removeProductModels(modelIdToDelete?.id)
  }

  const closeModal = () => {
    props.actions.clearProductModelsMessages()
    setSelectedModelDetails(null)
    setShowAddEditProductModelsModal(false)
    setShowViewProductModelsModal(false)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderStatus = (status) => {
    switch (status) {
      case Statuses[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case Statuses[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper flex-justify-between">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={
              selectedFilterModel ||
              selectedFilterProductType ||
              selectedFilterCategory ||
              selectedFilterDeviceFunction ||
              statusesFilter.length > 0 ||
              selectedBrandFilter.length > 0 ||
              selectedTechnologyFilter.length > 0
            }
            counterBadge={
              (selectedFilterModel && 1) +
              (selectedFilterProductType && 1) +
              (selectedFilterCategory && 1) +
              (selectedFilterDeviceFunction && 1) +
              selectedBrandFilter.length +
              statusesFilter.length +
              selectedTechnologyFilter.length
            }
            minSearchLength={2}
          />
          <SimpleButton className="submit-button" onClick={() => setShowAddEditProductModelsModal(true)} requiredPermission={productModelsWritePermissions}>
            {t('product_models_context.add_new_model')}
          </SimpleButton>
        </div>

        {isMobile && productModelsTableData?.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}

        {!isMobile && productModelsTableData?.length > 0 && (
          <GenericTable
            data={productModelsTableData}
            headers={[
              {
                title: t('model'),
                orderKey: 'name',
              },
              {
                title: t('product_type'),
                orderKey: 'productType',
              },
              {
                title: t('category'),
                orderKey: 'category',
              },
              {
                title: t('product_models_context.device_function'),
                orderKey: 'deviceFunction',
              },
              {
                title: t('product_models_context.technology'),
                orderKey: 'technology',
              },
              {
                title: t('status'),
                orderKey: 'isActive',
              },
              {
                title: '',
              },
            ]}
            keys={['name', 'productType', 'category', 'deviceFunction', 'technology', 'status', 'action']}
            keyRenderer={{
              status: (item) => {
                return <div className="status-wrapper">{renderStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(item) => handleOpenViewEdit(item._original)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}

        {isMobile &&
          productModelsTableData?.map((item, index) => (
            <div key={index} className="card" onClick={() => setExpandedVariant(index)}>
              <div className={'card-item' + (expandedVariant !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('model')}</div>
                <div className={'card-item-body' + (expandedVariant !== index ? ' align-center' : '')}>
                  <div>{item.name}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedVariant === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('product_type')}</div>
                    <div className="card-item-body">{item.productType}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('category')}</div>
                    <div className="card-item-body">{item.category}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('product_models_context.device_function')}</div>
                    <div className="card-item-body">{item.deviceFunction}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('product_models_context.technology')}</div>
                    <div className="card-item-body">{item.technology}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderStatus(item.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}

        {!showSpinner && !isFirstLoad && productModelsTableData?.length === 0 && (
          <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
        )}

        {canLoadMore && productModelsTableData.length > 0 && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>

      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: modelLabels,
              input: selectedFilterModel,
              output: (model) => {
                setSelectedFilterModel(model)
              },
            },
            {
              name: t('product_type'),
              type: FilterType.singleSelect,
              dataset: productTypeLabels,
              input: selectedFilterProductType,
              output: (type) => {
                setSelectedFilterProductType(type)
              },
            },
            {
              name: t('category'),
              type: FilterType.singleSelect,
              dataset: categoryLabels,
              input: selectedFilterCategory,
              output: (category) => {
                setSelectedFilterCategory(category)
              },
            },
            {
              name: t('product_models_context.device_function'),
              type: FilterType.singleSelect,
              dataset: deviceFunctionLabels,
              input: selectedFilterDeviceFunction,
              output: (deviceFunction) => {
                setSelectedFilterDeviceFunction(deviceFunction)
              },
            },
            {
              name: t('dealer-portal.brand'),
              type: FilterType.multiSelect,
              dataset: brandLabels,
              input: selectedBrandFilter,
              output: (brand) => {
                setSelectedBrandFilter(brand)
              },
            },
            {
              name: t('product_models_context.technology'),
              type: FilterType.multiSelect,
              dataset: technologyLabels,
              input: selectedTechnologyFilter,
              output: (technology) => {
                setSelectedTechnologyFilter(technology)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: Statuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}

      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}

      {showAddEditProductModelsModal && (
        <AddEditProductModels modelDetails={selectedModelDetails} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}

      {showViewProductModelsModal && selectedModelDetails && (
        <ViewProductModels
          modelDetails={selectedModelDetails}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditModel={() => {
            setShowViewProductModelsModal(false)
            setShowAddEditProductModelsModal(true)
          }}
          closeModal={closeModal}
        />
      )}

      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteConfirmModal(false)}
          onAccept={() => handleDeleteModel()}
          onCancel={() => setShowDeleteConfirmModal(false)}
          message={t('product_models_context.delete_model_confirmation')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={productModelsDeletePermissions}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ productModels }) {
  return {
    productModelsData: productModels?.productModels || [],
    models: productModels?.productModelsFilterOptions?.models || [],
    productTypes: productModels?.productModelsFilterOptions?.productTypes || [],
    productCategories: productModels?.productModelsFilterOptions?.productCategories || [],
    technologies: productModels?.productModelsFilterOptions?.technologies || [],
    brands: productModels?.productModelsFilterOptions?.brands || [],
    deviceFunctions: productModels?.productModelsFilterOptions?.deviceFunctions || [],
    isLoading: productModels?.isLoading,
    canLoadMore: productModels?.canLoadMore,
    successMessage: productModels?.successMessage,
    errorMessage: productModels?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadProductModels,
        removeProductModels,
        clearProductModelsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Models)

